import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import adminSchemaApi from '../api/adminSchemaApi';
import { formatCurrencyLabel } from '../../utils/formatUtils';
import '../styles/admin-theme.css';

const AdminPricebooks = () => {
  const navigate = useNavigate();
  const [pricebooks, setPricebooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', currency: 'USD', is_active: true });

  const loadPricebooks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminSchemaApi.fetchPricebooks();
      setPricebooks(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load pricebooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricebooks();
  }, [loadPricebooks]);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const record = await adminSchemaApi.createPricebook(createForm);
      setShowCreateModal(false);
      setCreateForm({ name: '', currency: 'USD', is_active: true });
      await loadPricebooks();
      if (record?.id) {
        navigate(`/admin/pricebooks/${record.id}`);
      }
    } catch (err) {
      setError(err?.message || 'Failed to create pricebook');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm({ name: '', currency: 'USD', is_active: true });
    setError('');
  };

  return (
    <>
      <h2 className="admin-title">Pricebooks</h2>
      <p className="admin-subtitle">
        Manage pricebooks (e.g. by country). Each pricebook can have entries per license type with list price, discount, and tax.
      </p>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <div className="mb-3">
        <Button
          className="admin-primary-btn"
          onClick={() => setShowCreateModal(true)}
        >
          New Pricebook
        </Button>
      </div>

      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create Pricebook</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. USA, India"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Currency</Form.Label>
              <Form.Select
                value={createForm.currency}
                onChange={(e) => setCreateForm((p) => ({ ...p, currency: e.target.value }))}
              >
                <option value="USD">USD ($)</option>
                <option value="INR">INR (₹)</option>
                <option value="AED">AED (د.إ)</option>
              </Form.Select>
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="Active"
              checked={createForm.is_active}
              onChange={(e) => setCreateForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseCreateModal}>Cancel</Button>
            <Button type="submit" className="admin-primary-btn" disabled={saving}>
              {saving ? 'Creating…' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {loading ? (
        <p className="admin-subtitle">Loading...</p>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Currency</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pricebooks.map((pb) => (
              <tr key={pb.id}>
                <td>{pb.name || '-'}</td>
                <td>{pb.currency ? formatCurrencyLabel(pb.currency) : '-'}</td>
                <td>{pb.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <Link to={`/admin/pricebooks/${pb.id}`} className="btn btn-sm btn-outline-primary me-1">
                    Manage entries
                  </Link>
                </td>
              </tr>
            ))}
            {pricebooks.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  No pricebooks. Create one to define prices per license type (e.g. by country).
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default AdminPricebooks;
